import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../services/api';
import {
    Box, Typography, Container, Paper, Grid, TextField, Button, CircularProgress,
    Fade, Grow, Card, CardContent, Alert, Chip, Divider, IconButton,
    ToggleButtonGroup, ToggleButton, MenuItem, Select, FormControl, InputLabel
} from '@mui/material';
import {
    Home as HomeIcon, Save, Download, Calculate, Add, Delete, Business, Apartment
} from '@mui/icons-material';

const fmt = (v) => (v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pctFmt = (v) => ((v || 0) * 100).toFixed(2) + '%';
const val = (v) => parseFloat(v) || 0;

function BusinessUseOfHome() {
    const currentYear = new Date().getFullYear();
    const [taxYear, setTaxYear] = useState(currentYear - 1);
    const [optionType, setOptionType] = useState('owned');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState(null);
    const [downloading, setDownloading] = useState(false);

    // Owned fields
    const [purchasePrice, setPurchasePrice] = useState('');
    const [landValue, setLandValue] = useState('');
    const [bizStartDate, setBizStartDate] = useState('');
    const [bizEndDate, setBizEndDate] = useState('');
    const [sqFtBusiness, setSqFtBusiness] = useState('');
    const [sqFtTotal, setSqFtTotal] = useState('');
    const [mortgageInterest, setMortgageInterest] = useState('');
    const [propertyTaxes, setPropertyTaxes] = useState('');
    const [hazardInsurance, setHazardInsurance] = useState('');
    const [utilities, setUtilities] = useState('');
    const [hoaDues, setHoaDues] = useState('');

    // Rented fields
    const [rentBizStartDate, setRentBizStartDate] = useState('');
    const [rentBizEndDate, setRentBizEndDate] = useState('');
    const [rentSqFtBusiness, setRentSqFtBusiness] = useState('');
    const [rentSqFtTotal, setRentSqFtTotal] = useState('');
    const [totalRent, setTotalRent] = useState('');
    const [rentUtilities, setRentUtilities] = useState('');
    const [rentHoaDues, setRentHoaDues] = useState('');
    const [renterInsurance, setRenterInsurance] = useState('');

    // Depreciation recapture
    const [depEntries, setDepEntries] = useState([]);

    useEffect(() => {
        setLoading(true);
        api.get('/api/home-office', { params: { taxYear } })
            .then(res => {
                if (res.status === 204 || !res.data) {
                    resetFields();
                    return;
                }
                const d = res.data;
                setOptionType(d.optionType || 'owned');
                setPurchasePrice(d.purchasePrice != null ? String(d.purchasePrice) : '');
                setLandValue(d.landValue != null ? String(d.landValue) : '');
                setBizStartDate(d.bizStartDate || '');
                setBizEndDate(d.bizEndDate || '');
                setSqFtBusiness(d.sqFtBusiness != null ? String(d.sqFtBusiness) : '');
                setSqFtTotal(d.sqFtTotal != null ? String(d.sqFtTotal) : '');
                setMortgageInterest(d.mortgageInterest != null ? String(d.mortgageInterest) : '');
                setPropertyTaxes(d.propertyTaxes != null ? String(d.propertyTaxes) : '');
                setHazardInsurance(d.hazardInsurance != null ? String(d.hazardInsurance) : '');
                setUtilities(d.utilities != null ? String(d.utilities) : '');
                setHoaDues(d.hoaDues != null ? String(d.hoaDues) : '');
                setRentBizStartDate(d.rentBizStartDate || '');
                setRentBizEndDate(d.rentBizEndDate || '');
                setRentSqFtBusiness(d.rentSqFtBusiness != null ? String(d.rentSqFtBusiness) : '');
                setRentSqFtTotal(d.rentSqFtTotal != null ? String(d.rentSqFtTotal) : '');
                setTotalRent(d.totalRent != null ? String(d.totalRent) : '');
                setRentUtilities(d.rentUtilities != null ? String(d.rentUtilities) : '');
                setRentHoaDues(d.rentHoaDues != null ? String(d.rentHoaDues) : '');
                setRenterInsurance(d.renterInsurance != null ? String(d.renterInsurance) : '');
                try {
                    setDepEntries(d.depreciationEntries ? JSON.parse(d.depreciationEntries) : []);
                } catch { setDepEntries([]); }
            })
            .catch(() => resetFields())
            .finally(() => setLoading(false));
    }, [taxYear]);

    const resetFields = () => {
        setOptionType('owned');
        setPurchasePrice(''); setLandValue('');
        setBizStartDate(''); setBizEndDate('');
        setSqFtBusiness(''); setSqFtTotal('');
        setMortgageInterest(''); setPropertyTaxes('');
        setHazardInsurance(''); setUtilities(''); setHoaDues('');
        setRentBizStartDate(''); setRentBizEndDate('');
        setRentSqFtBusiness(''); setRentSqFtTotal('');
        setTotalRent(''); setRentUtilities('');
        setRentHoaDues(''); setRenterInsurance('');
        setDepEntries([]);
    };

    // Computed values (owned)
    const depreciable = val(purchasePrice) - val(landValue);
    const pctOwned = val(sqFtTotal) > 0 ? val(sqFtBusiness) / val(sqFtTotal) : 0;
    const depreciation = depreciable > 0 ? depreciable * pctOwned / 39 : 0;

    const ownedExpenses = useMemo(() => {
        const items = [
            { label: 'Mortgage Interest (Form 1098)', amount: val(mortgageInterest), scheduleA: true },
            { label: 'Property Taxes', amount: val(propertyTaxes), scheduleA: true },
            { label: 'Hazard Insurance', amount: val(hazardInsurance), scheduleA: false },
            { label: 'Utilities', amount: val(utilities), scheduleA: false },
            { label: 'HOA Dues', amount: val(hoaDues), scheduleA: false },
        ];
        const expenseTotal = items.reduce((s, i) => s + i.amount * pctOwned, 0) + depreciation;
        return { items, depreciation, total: expenseTotal };
    }, [mortgageInterest, propertyTaxes, hazardInsurance, utilities, hoaDues, pctOwned, depreciation]);

    // Computed values (rented)
    const pctRented = val(rentSqFtTotal) > 0 ? val(rentSqFtBusiness) / val(rentSqFtTotal) : 0;

    const rentedExpenses = useMemo(() => {
        const items = [
            { label: 'Total Rent', amount: val(totalRent) },
            { label: 'Utilities', amount: val(rentUtilities) },
            { label: 'HOA Dues', amount: val(rentHoaDues) },
            { label: 'Renter Insurance', amount: val(renterInsurance) },
        ];
        const total = items.reduce((s, i) => s + i.amount * pctRented, 0);
        return { items, total };
    }, [totalRent, rentUtilities, rentHoaDues, renterInsurance, pctRented]);

    const handleSave = useCallback(() => {
        setSaving(true);
        setSaveStatus(null);
        const payload = {
            taxYear, optionType,
            purchasePrice: val(purchasePrice) || null,
            landValue: val(landValue) || null,
            bizStartDate: bizStartDate || null,
            bizEndDate: bizEndDate || null,
            sqFtBusiness: val(sqFtBusiness) || null,
            sqFtTotal: val(sqFtTotal) || null,
            mortgageInterest: val(mortgageInterest) || null,
            propertyTaxes: val(propertyTaxes) || null,
            hazardInsurance: val(hazardInsurance) || null,
            utilities: val(utilities) || null,
            hoaDues: val(hoaDues) || null,
            rentBizStartDate: rentBizStartDate || null,
            rentBizEndDate: rentBizEndDate || null,
            rentSqFtBusiness: val(rentSqFtBusiness) || null,
            rentSqFtTotal: val(rentSqFtTotal) || null,
            totalRent: val(totalRent) || null,
            rentUtilities: val(rentUtilities) || null,
            rentHoaDues: val(rentHoaDues) || null,
            renterInsurance: val(renterInsurance) || null,
            depreciationEntries: JSON.stringify(depEntries),
        };
        api.post('/api/home-office', payload)
            .then(() => setSaveStatus('success'))
            .catch(() => setSaveStatus('error'))
            .finally(() => setSaving(false));
    }, [taxYear, optionType, purchasePrice, landValue, bizStartDate, bizEndDate,
        sqFtBusiness, sqFtTotal, mortgageInterest, propertyTaxes, hazardInsurance,
        utilities, hoaDues, rentBizStartDate, rentBizEndDate, rentSqFtBusiness,
        rentSqFtTotal, totalRent, rentUtilities, rentHoaDues, renterInsurance, depEntries]);

    const handleDownload = useCallback(() => {
        setDownloading(true);
        api.get('/api/home-office/download', { params: { taxYear }, responseType: 'blob' })
            .then(res => {
                const url = URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a');
                a.href = url;
                a.download = `BusinessUseofHome_${taxYear}.xls`;
                a.click();
                URL.revokeObjectURL(url);
            })
            .catch(err => console.error('Download error:', err))
            .finally(() => setDownloading(false));
    }, [taxYear]);

    const addDepEntry = () => setDepEntries(prev => [...prev, { year: '', amount: '' }]);
    const removeDepEntry = (i) => setDepEntries(prev => prev.filter((_, idx) => idx !== i));
    const updateDepEntry = (i, field, value) => setDepEntries(prev =>
        prev.map((e, idx) => idx === i ? { ...e, [field]: value } : e)
    );
    const depTotal = depEntries.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);

    const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

    const InputField = ({ label, value, onChange, type = 'number', prefix, ...rest }) => (
        <TextField
            fullWidth size="small" label={label} value={value}
            onChange={(e) => onChange(e.target.value)}
            type={type}
            InputProps={prefix ? { startAdornment: <Typography sx={{ mr: 0.5, color: '#999' }}>{prefix}</Typography> } : undefined}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#fffde7' } }}
            {...rest}
        />
    );

    const ComputedField = ({ label, value }) => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, px: 2, backgroundColor: '#e3f2fd', borderRadius: 1.5, mb: 0.5 }}>
            <Typography variant="body2" fontWeight={600} color="text.secondary">{label}</Typography>
            <Typography variant="body2" fontWeight={700} color="primary">{value}</Typography>
        </Box>
    );

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', py: 4 }}>
            <Container maxWidth="lg">
                {/* Hero */}
                <Fade in timeout={800}>
                    <Paper elevation={0} sx={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white', borderRadius: 4, p: 4, mb: 4, position: 'relative', overflow: 'hidden' }}>
                        <Box position="relative" zIndex={1}>
                            <Box display="flex" alignItems="center" mb={1}>
                                <HomeIcon sx={{ fontSize: 40, mr: 2 }} />
                                <Typography variant="h3" fontWeight={700}>Business Use of Home</Typography>
                            </Box>
                            <Typography variant="h6" sx={{ opacity: 0.9 }}>
                                Home office deduction worksheet — IRS Form 8829
                            </Typography>
                        </Box>
                        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', filter: 'blur(60px)' }} />
                    </Paper>
                </Fade>

                {/* Controls */}
                <Fade in timeout={1000}>
                    <Paper elevation={0} sx={{ borderRadius: 3, p: 3, mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                            <Box display="flex" alignItems="center" gap={2}>
                                <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel>Tax Year</InputLabel>
                                    <Select value={taxYear} label="Tax Year" onChange={(e) => setTaxYear(Number(e.target.value))} sx={{ fontWeight: 600 }}>
                                        {yearOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <Divider orientation="vertical" flexItem />
                                <Typography variant="subtitle2" fontWeight={600}>Deduction Method</Typography>
                                <ToggleButtonGroup value={optionType} exclusive onChange={(_, v) => { if (v) setOptionType(v); }} size="small"
                                    sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 2, fontWeight: 600 } }}>
                                    <ToggleButton value="simplified" sx={{ '&.Mui-selected': { backgroundColor: 'rgba(17,153,142,0.1)', color: '#11998e' } }}>
                                        Simplified ($5/sq ft)
                                    </ToggleButton>
                                    <ToggleButton value="owned" sx={{ '&.Mui-selected': { backgroundColor: 'rgba(17,153,142,0.1)', color: '#11998e' } }}>
                                        <Business sx={{ mr: 0.5, fontSize: 18 }} /> Owned
                                    </ToggleButton>
                                    <ToggleButton value="rented" sx={{ '&.Mui-selected': { backgroundColor: 'rgba(17,153,142,0.1)', color: '#11998e' } }}>
                                        <Apartment sx={{ mr: 0.5, fontSize: 18 }} /> Rented
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                            <Box display="flex" gap={1.5}>
                                {saveStatus === 'success' && <Chip label="Saved!" size="small" sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />}
                                {saveStatus === 'error' && <Chip label="Save failed" size="small" sx={{ backgroundColor: '#ffebee', color: '#c62828', fontWeight: 600 }} />}
                                <Button variant="outlined" startIcon={saving ? <CircularProgress size={16} /> : <Save />}
                                    onClick={handleSave} disabled={saving}
                                    sx={{ borderColor: '#11998e', color: '#11998e', fontWeight: 600, textTransform: 'none', '&:hover': { borderColor: '#0d7a6e', backgroundColor: 'rgba(17,153,142,0.08)' } }}>
                                    {saving ? 'Saving...' : 'Save'}
                                </Button>
                                <Button variant="contained" startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <Download />}
                                    onClick={handleDownload} disabled={downloading}
                                    sx={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', fontWeight: 600, textTransform: 'none', boxShadow: '0 4px 15px rgba(17,153,142,0.4)' }}>
                                    {downloading ? 'Generating...' : 'Download XLS'}
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                </Fade>

                {/* Simplified Option */}
                {optionType === 'simplified' && (
                    <Fade in timeout={600}>
                        <Paper elevation={0} sx={{ borderRadius: 3, p: 4, mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '2px solid #11998e' }}>
                            <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                                <Calculate sx={{ color: '#11998e', fontSize: 28 }} />
                                <Box>
                                    <Typography variant="h6" fontWeight={700}>Option A — Simplified Method</Typography>
                                    <Typography variant="caption" color="text.secondary">$5 per sq ft, max 300 sq ft = $1,500 maximum deduction</Typography>
                                </Box>
                            </Box>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <InputField label="Square Feet Used for Business (max 300)" value={sqFtBusiness} onChange={setSqFtBusiness} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <ComputedField label="Deduction" value={`$${fmt(Math.min(val(sqFtBusiness), 300) * 5)}`} />
                                </Grid>
                            </Grid>
                            <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                                With the simplified method, home-related itemized deductions (mortgage interest, property taxes) can be claimed in full on Schedule A.
                                No depreciation deduction or recapture applies.
                            </Alert>
                        </Paper>
                    </Fade>
                )}

                {/* Owned Property Section */}
                {optionType === 'owned' && (
                    <Fade in timeout={600}>
                        <Box>
                            <Paper elevation={0} sx={{ borderRadius: 3, p: 4, mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                                <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                                    <Box sx={{ p: 1, borderRadius: 2, background: 'linear-gradient(135deg, rgba(17,153,142,0.15) 0%, rgba(56,239,125,0.15) 100%)' }}>
                                        <Business sx={{ color: '#11998e', fontSize: 28 }} />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>Option B — Owned Property</Typography>
                                        <Typography variant="caption" color="text.secondary">Actual expense method with depreciation</Typography>
                                    </Box>
                                </Box>

                                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                                    Property Details
                                </Typography>
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={12} md={4}>
                                        <InputField label="Purchase Price of Home" value={purchasePrice} onChange={setPurchasePrice} prefix="$" />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <InputField label="Less: Land Value" value={landValue} onChange={setLandValue} prefix="$" />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <ComputedField label="Total Depreciable Amount" value={`$${fmt(depreciable)}`} />
                                    </Grid>
                                </Grid>

                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={12} md={3}>
                                        <InputField label="Business Use Start Date" value={bizStartDate} onChange={setBizStartDate} type="date" InputLabelProps={{ shrink: true }} />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <InputField label="Business Use End Date" value={bizEndDate} onChange={setBizEndDate} type="date" InputLabelProps={{ shrink: true }} />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <InputField label="Sq Ft for Business" value={sqFtBusiness} onChange={setSqFtBusiness} />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <InputField label="Total Home Sq Ft" value={sqFtTotal} onChange={setSqFtTotal} />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <ComputedField label="% Used" value={pctFmt(pctOwned)} />
                                    </Grid>
                                </Grid>

                                <Divider sx={{ mb: 3 }} />
                                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                                    Annual Expenses
                                </Typography>
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid item xs={12} md={4}><InputField label="Mortgage Interest (Form 1098)" value={mortgageInterest} onChange={setMortgageInterest} prefix="$" /></Grid>
                                    <Grid item xs={12} md={4}><InputField label="Property Taxes" value={propertyTaxes} onChange={setPropertyTaxes} prefix="$" /></Grid>
                                    <Grid item xs={12} md={4}><InputField label="Hazard Insurance" value={hazardInsurance} onChange={setHazardInsurance} prefix="$" /></Grid>
                                    <Grid item xs={12} md={4}><InputField label="Utilities" value={utilities} onChange={setUtilities} prefix="$" /></Grid>
                                    <Grid item xs={12} md={4}><InputField label="HOA Dues" value={hoaDues} onChange={setHoaDues} prefix="$" /></Grid>
                                </Grid>

                                <Divider sx={{ mb: 3 }} />
                                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}>
                                    Calculated Deductions
                                </Typography>
                                <Box sx={{ maxWidth: 600 }}>
                                    {ownedExpenses.items.map((item, i) => (
                                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #f0f0f0' }}>
                                            <Typography variant="body2">{item.label}</Typography>
                                            <Box display="flex" gap={3}>
                                                <Typography variant="body2" fontWeight={600} color="#11998e">
                                                    Biz: ${fmt(item.amount * pctOwned)}
                                                </Typography>
                                                {item.scheduleA && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Sch A: ${fmt(item.amount * (1 - pctOwned))}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    ))}
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #f0f0f0' }}>
                                        <Typography variant="body2">Depreciation (39-yr straight-line)</Typography>
                                        <Typography variant="body2" fontWeight={600} color="#11998e">
                                            Biz: ${fmt(depreciation)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, mt: 1, backgroundColor: '#e8f5e9', borderRadius: 1.5, px: 2 }}>
                                        <Typography variant="subtitle2" fontWeight={700}>Total Home Office Deduction</Typography>
                                        <Typography variant="subtitle2" fontWeight={700} color="#2e7d32">${fmt(ownedExpenses.total)}</Typography>
                                    </Box>
                                </Box>
                            </Paper>

                            {/* Depreciation Recapture */}
                            <Paper elevation={0} sx={{ borderRadius: 3, p: 4, mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                    <Typography variant="h6" fontWeight={700}>Depreciation Recapture Summary</Typography>
                                    <Button startIcon={<Add />} onClick={addDepEntry} size="small"
                                        sx={{ textTransform: 'none', fontWeight: 600, color: '#11998e' }}>
                                        Add Year
                                    </Button>
                                </Box>
                                {depEntries.map((entry, i) => (
                                    <Box key={i} display="flex" gap={2} alignItems="center" mb={1}>
                                        <TextField size="small" label="Year" value={entry.year}
                                            onChange={(e) => updateDepEntry(i, 'year', e.target.value)}
                                            sx={{ width: 120, '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#fffde7' } }} />
                                        <TextField size="small" label="Amount" type="number" value={entry.amount}
                                            onChange={(e) => updateDepEntry(i, 'amount', e.target.value)}
                                            InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: '#999' }}>$</Typography> }}
                                            sx={{ width: 200, '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: '#fffde7' } }} />
                                        <IconButton size="small" onClick={() => removeDepEntry(i)} sx={{ color: '#e53935' }}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))}
                                {depEntries.length > 0 && (
                                    <ComputedField label="Total Depreciation Claimed" value={`$${fmt(depTotal)}`} />
                                )}
                                {depEntries.length === 0 && (
                                    <Typography variant="body2" color="text.disabled">
                                        No depreciation history. Click "Add Year" to track prior depreciation for recapture purposes.
                                    </Typography>
                                )}
                            </Paper>
                        </Box>
                    </Fade>
                )}

                {/* Rented Property Section */}
                {optionType === 'rented' && (
                    <Fade in timeout={600}>
                        <Paper elevation={0} sx={{ borderRadius: 3, p: 4, mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                            <Box display="flex" alignItems="center" gap={1.5} mb={3}>
                                <Box sx={{ p: 1, borderRadius: 2, background: 'linear-gradient(135deg, rgba(17,153,142,0.15) 0%, rgba(56,239,125,0.15) 100%)' }}>
                                    <Apartment sx={{ color: '#11998e', fontSize: 28 }} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight={700}>Option C — Rented Property</Typography>
                                    <Typography variant="caption" color="text.secondary">Home office expenses for rented home</Typography>
                                </Box>
                            </Box>

                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} md={3}>
                                    <InputField label="Business Use Start Date" value={rentBizStartDate} onChange={setRentBizStartDate} type="date" InputLabelProps={{ shrink: true }} />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <InputField label="Business Use End Date" value={rentBizEndDate} onChange={setRentBizEndDate} type="date" InputLabelProps={{ shrink: true }} />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <InputField label="Sq Ft for Business" value={rentSqFtBusiness} onChange={setRentSqFtBusiness} />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <InputField label="Total Home Sq Ft" value={rentSqFtTotal} onChange={setRentSqFtTotal} />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <ComputedField label="% Used" value={pctFmt(pctRented)} />
                                </Grid>
                            </Grid>

                            <Divider sx={{ mb: 3 }} />
                            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                                Annual Expenses
                            </Typography>
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} md={3}><InputField label="Total Rent" value={totalRent} onChange={setTotalRent} prefix="$" /></Grid>
                                <Grid item xs={12} md={3}><InputField label="Utilities" value={rentUtilities} onChange={setRentUtilities} prefix="$" /></Grid>
                                <Grid item xs={12} md={3}><InputField label="HOA Dues" value={rentHoaDues} onChange={setRentHoaDues} prefix="$" /></Grid>
                                <Grid item xs={12} md={3}><InputField label="Renter Insurance" value={renterInsurance} onChange={setRenterInsurance} prefix="$" /></Grid>
                            </Grid>

                            <Divider sx={{ mb: 3 }} />
                            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}>
                                Calculated Deductions
                            </Typography>
                            <Box sx={{ maxWidth: 500 }}>
                                {rentedExpenses.items.map((item, i) => (
                                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #f0f0f0' }}>
                                        <Typography variant="body2">{item.label}</Typography>
                                        <Typography variant="body2" fontWeight={600} color="#11998e">
                                            Biz: ${fmt(item.amount * pctRented)}
                                        </Typography>
                                    </Box>
                                ))}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, mt: 1, backgroundColor: '#e8f5e9', borderRadius: 1.5, px: 2 }}>
                                    <Typography variant="subtitle2" fontWeight={700}>Total Home Office Deduction</Typography>
                                    <Typography variant="subtitle2" fontWeight={700} color="#2e7d32">${fmt(rentedExpenses.total)}</Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Fade>
                )}
            </Container>
        </Box>
    );
}

export default BusinessUseOfHome;
